import React, { useState, useEffect, useCallback, useContext } from 'react';
import PropTypes from 'prop-types';
import { Checkbox, Input, Tree } from 'syngenta-digital-cropwise-react-ui-kit';
import LoadingSpinner from 'components/LoadingSpinner';
import { obtainAllKeys } from 'screens/Reports/helpers/helperFunctions';
import { intersection } from 'lodash';
import { useFormikContext } from 'formik';
import { ReportsContext } from 'screens/Reports/context/ReportsProvider';
import './index.css';

const productTypeDisplay = key => {
  switch (key) {
    case 'SEED':
      return 'Seed';
    case 'CROPPROTECTION':
      return 'Crop Protection';
    case 'FERTILIZER':
      return 'Fertilizer';
    case 'SERVICE':
      return 'Service';
    default:
      return 'Custom Product';
  }
};

const groupByProductsDefaultPropMappedData = [];
const toTreeData = nodes =>
  nodes.map(node => ({
    ...node,
    key: node.value,
    title: node.label,
    children: node.children ? toTreeData(node.children) : undefined
  }));

const renderSwitcher = ({ expanded }) => (
  <svg aria-hidden="true" width="16" height="20" viewBox="0 0 24 24">
    <path
      fill="#696f88"
      d={expanded ? 'M7 10l5 5 5-5z' : 'M10 17l5-5-5-5v10z'}
    />
  </svg>
);

const GroupByProducts = ({
  mappedData = groupByProductsDefaultPropMappedData,
  disabled = false,
  loading = false
}) => {
  const { setFieldValue } = useFormikContext();
  const {
    state: { formSelections }
  } = useContext(ReportsContext);

  const [checked, setChecked] = useState([]);
  const [expanded, setExpanded] = useState([]);
  const [filterText, setFilterText] = useState();
  const [productsNode, setProductsNode] = useState([]);
  const [nodesFiltered, setNodesFiltered] = useState(productsNode);

  useEffect(() => {
    let mappedFft = [];
    if (mappedData) {
      mappedFft = mappedData?.map((eachProduct, parentIndex) => ({
        value: `CP_${parentIndex}`,
        label: productTypeDisplay(eachProduct.productType),
        type: 'type',
        ...(eachProduct.children.length > 0
          ? {
              children: eachProduct.children?.map(item => {
                return {
                  id: item.productId,
                  value: `PN_${item.productId}`,
                  label: item.productName,
                  type: 'name'
                };
              })
            }
          : { disabled: true })
      }));
    }
    setProductsNode(mappedFft);
  }, [mappedData]);

  const handleCheck = checkedIds => {
    setChecked(checkedIds);
  };
  useEffect(() => {
    // A product type with no children is mapped without a `children` key, so
    // flattening leaves a hole that the lookup below would dereference.
    const products = productsNode.flatMap(({ children }) => children ?? []);

    const productMatches = checked
      .filter(id => id.startsWith('PN_'))
      .map(_id => products.find(product => product.value === _id))
      .filter(product => !!product)
      .map(({ id }) => id);

    setFieldValue('checkedProducts', checked);
    setFieldValue('selectedProducts', [...new Set(productMatches)]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checked]);

  useEffect(() => {
    handleCheck();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productsNode]);

  const filterNodes = useCallback(
    (filtered, node) => {
      const children = (node.children || []).reduce(
        (acc, current) => filterNodes(acc, current),
        []
      );

      if (
        node.label
          .toLocaleLowerCase()
          .indexOf(filterText?.toLocaleLowerCase()) > -1 ||
        children.length
      ) {
        filtered.push({ ...node, children });
      }

      return filtered;
    },
    [filterText]
  );

  const filterTree = () => {
    if (!filterText?.length) {
      setNodesFiltered(productsNode);
      return;
    }

    setNodesFiltered(nodes =>
      nodes.reduce((acc, current) => filterNodes(acc, current), [])
    );
  };

  const onFilterChange = e => {
    if (e.target.value.length >= 3) {
      setFilterText(e.target.value);
      filterTree();
    } else {
      setFilterText('');
    }
  };

  useEffect(() => {
    if (!filterText) {
      setNodesFiltered(productsNode);
      return;
    }

    setNodesFiltered(prevState =>
      prevState.reduce((acc, current) => filterNodes(acc, current), [])
    );
  }, [filterText, filterNodes, productsNode]);

  useEffect(() => {
    const allKeys = obtainAllKeys(productsNode);
    if (intersection(formSelections.products, allKeys).length > 0) {
      setChecked(formSelections.products);
    } else {
      setChecked(obtainAllKeys(productsNode));
    }
  }, [productsNode, formSelections.products]);

  const productKeys = obtainAllKeys(productsNode);

  const handleTreeCheck = checkedKeys => {
    const visibleKeys = new Set(obtainAllKeys(nodesFiltered));
    const disabledKeys = new Set(
      productsNode.filter(node => node.disabled).map(node => node.value)
    );
    const nextKeys = new Set([
      ...checkedKeys,
      ...(checked ?? []).filter(
        key => !visibleKeys.has(key) || disabledKeys.has(key)
      )
    ]);
    handleCheck(productKeys.filter(key => nextKeys.has(key)));
  };

  const handleSelectAllCheckBox = () => {
    if (checked.length === productKeys.length) {
      setChecked([]);
    } else {
      setChecked(productKeys);
    }
  };
  return (
    <div className="filter-container reports-product-filter h-full">
      {loading ? (
        <LoadingSpinner
          message=""
          size={25}
          inline
          className="h-full"
          label="Loading products"
        />
      ) : (
        <>
          {mappedData?.length > 0 && (
            <>
              <div
                className="my-2"
                data-testid="product-filter-container"
                style={{ display: 'none' }}
              >
                <Input
                  allowClear
                  placeholder="Search..."
                  onChange={onFilterChange}
                />
              </div>
              <div className="mt-2 flex">
                <Checkbox
                  className="reports-product-filter__select-all"
                  onChange={handleSelectAllCheckBox}
                  checked={checked?.length === productKeys?.length}
                >
                  Select All
                </Checkbox>
              </div>

              <Tree
                checkable
                treeData={toTreeData(nodesFiltered)}
                checkedKeys={checked ?? []}
                expandedKeys={expanded}
                switcherIcon={renderSwitcher}
                onCheck={handleTreeCheck}
                onExpand={setExpanded}
                selectable={false}
                showIcon={false}
                disabled={disabled}
              />
            </>
          )}
        </>
      )}
    </div>
  );
};

GroupByProducts.propTypes = {
  mappedData: PropTypes.arrayOf(PropTypes.object),
  disabled: PropTypes.bool,
  loading: PropTypes.bool
};

export default GroupByProducts;
